/**
 * RedemptionTask — Phase 20.7 (Weekend Redemption Protocol)
 * -------------------------------------------------------------------
 * Renders the auto-assigned weekend redemption directive on the target
 * user's dashboard. The card NEVER references the 50-pt threshold; that
 * value is computed server-side and never reaches the client payload.
 *
 * Shape:  [ WEEKEND REDEMPTION ACTIVE ]
 *         Directive: <task>
 *         Deadline:  SUN 23:59     (live HH:MM:SS countdown)
 *         [ MISSION ACCOMPLISHED ]  (single button — optimistic dismiss)
 *
 * Optimistic UI: clicking the button instantly fires `onChange` + clears
 * the local card; rollback restores it on backend error.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function useDeadlineCountdown(expiresAtIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAtIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAtIso]);
  if (!expiresAtIso) return null;
  const remaining = Math.max(0, new Date(expiresAtIso).getTime() - now);
  const h = Math.floor(remaining / 3_600_000);
  const m = Math.floor((remaining % 3_600_000) / 60_000);
  const s = Math.floor((remaining % 60_000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function RedemptionTask({ userId, leagueId, onChange }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 20.12 — live-camera proof for redemption claims
  const [uploading, setUploading] = useState(false);
  const [photoPath, setPhotoPath] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getRedemption(userId, leagueId);
      setInfo(data);
      // Hydrate any previously-attached proof for this week's claim so a
      // page refresh restores the thumbnail.
      if (data?.proof_path && !photoPath) setPhotoPath(data.proof_path);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[RedemptionTask] poll failed, retaining prior state:",
        err?.response?.status,
        err?.message,
      );
    }
  }, [userId, leagueId, photoPath]);

  useEffect(() => {
    if (!userId || !leagueId) return;
    load();
    // Phase 20.11 — poll every 30s so a freshly-auto-assigned redemption
    // (e.g., the Friday 18:00 sweep firing while the user is on the
    // dashboard) surfaces without requiring a manual reload. Hardened
    // catch in `load()` ensures a single failed poll never wipes the card.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [userId, leagueId, load]);

  const countdown = useDeadlineCountdown(info?.expires_at);

  const onAccomplished = async () => {
    // ---------- Phase 20.6/20.7 — Optimistic dismiss ----------
    const prev = info;
    setInfo({ ...info, completed: true });   // instant tick
    setBusy(true);
    try {
      const updated = await api.completeRedemption(userId, leagueId);
      setInfo(updated);
      toast.success(`+${updated.bounty} PTS · MISSION ACCOMPLISHED`);
      onChange?.();
    } catch (e) {
      setInfo(prev);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `[ REVERTED: ${String(detail).toUpperCase()} ]` : "[ REVERTED: NETWORK ERROR ]");
    } finally { setBusy(false); }
  };

  const onAttachProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const updated = await api.uploadCheckinPhoto(userId, today, 99, file);
      setPhotoPath(updated?.photo_path || null);
      toast.success("PROOF ATTACHED");
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `[ UPLOAD FAILED: ${String(detail).toUpperCase()} ]` : "[ UPLOAD FAILED ]");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Hide entirely if no bounty (= not a target) or no task assigned yet
  if (!info || info.bounty <= 0 || !(info.task && info.task.trim())) return null;

  const inWindow = !!info.in_eligibility_window;
  const completed = !!info.completed;
  const accent = "var(--stakes-primary)";  // red — high-contrast alert

  return (
    <div
      className="border-2 p-4 md:p-5 relative overflow-hidden"
      style={{ borderColor: accent, background: "rgba(0,0,0,0.55)", boxShadow: `0 0 28px ${accent}33` }}
      data-testid="redemption-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono" style={{ color: accent }}>
          {completed ? "[ REDEMPTION COMPLETE ]" : "[ WEEKEND REDEMPTION ACTIVE ]"}
        </div>
        {!completed && countdown && inWindow && (
          <div className="label-mono tabular-nums" data-testid="redemption-countdown">
            DEADLINE: SUN 23:59 · <span style={{ color: accent }}>{countdown}</span>
          </div>
        )}
        {!inWindow && !completed && (
          <div className="label-mono text-[var(--stakes-muted)]" data-testid="redemption-window-pending">
            OPENS FRIDAY 18:00
          </div>
        )}
      </div>

      <div className="label-mono mt-3 text-[var(--stakes-muted)]">DIRECTIVE</div>
      <h3 className="font-anton text-xl md:text-2xl uppercase mt-1 leading-snug" data-testid="redemption-task-text">
        {info.task}
      </h3>

      <button
        onClick={onAccomplished}
        disabled={busy || completed || !info.eligible}
        className="mt-4 w-full bg-[var(--stakes-primary)] text-black font-anton uppercase tracking-wider text-xl py-4 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-[filter]"
        data-testid="redemption-complete-button"
      >
        {completed
          ? `[ +${info.bounty} CLAIMED ]`
          : !inWindow
          ? "[ LOCKED · OPENS FRIDAY 18:00 ]"
          : "[ MISSION ACCOMPLISHED ]"}
      </button>

      {/* Phase 20.12 — Live-camera proof. Mirrors the standard check-in
          photo flow (capture="environment" hint opens the rear camera on
          mobile). Available the moment a directive is assigned — claim
          first OR attach proof first, both work. Hidden until eligible
          window opens so users aren't tempted to pre-stage fake evidence. */}
      {inWindow && (
        <div className="mt-3 flex items-center gap-3" data-testid="redemption-proof-row">
          {photoPath ? (
            <a
              href={api.fileUrl(photoPath)}
              target="_blank"
              rel="noreferrer"
              className="block"
              data-testid="redemption-proof-thumb"
            >
              <img
                src={api.fileUrl(photoPath)}
                alt="proof"
                className="w-12 h-12 object-cover border-2 border-[var(--neon-blue)]"
              />
            </a>
          ) : (
            <div className="w-12 h-12 flex items-center justify-center border-2 border-dashed border-[var(--stakes-border)] label-mono text-[var(--stakes-muted)]">
              —
            </div>
          )}
          <label
            className={`label-mono flex-1 px-3 py-2 border border-[var(--stakes-border)] cursor-pointer hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] text-center transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="redemption-attach-proof"
          >
            {uploading ? "[ UPLOADING… ]" : photoPath ? "[ 📷 REPLACE PROOF ]" : "[ 📷 ATTACH PROOF ]"}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => onAttachProof(e.target.files?.[0])}
              data-testid="redemption-proof-input"
            />
          </label>
        </div>
      )}
    </div>
  );
}
